import { useState, useCallback, useEffect } from "react";
import { listRoles } from "../../../api/accessManagement/rolesService";
import type { RoleFormData } from "../../../components/accessManagement/organisms/CreateRoleDrawer";
import type { RoleDrawerMode } from "../../../components/accessManagement/organisms/CreateRoleDrawer";

function mapRoleToFormData(role: {
  _id: string;
  code: string;
  name: string;
  scopeType?: string;
  /** New RBAC field: list of allowed scope types, e.g. ["academy"] */
  allowedScopeTypes?: string[];
  description?: string;
  permissionsCount?: number;
  /** Some APIs may expose this as permissionCount (singular) */
  permissionCount?: number;
  createdBy?: string;
  createdByName?: string;
  permissionCodes?: string[];
  permissions?: { code: string }[];
}): RoleFormData {
  // Prefer explicit permissionCodes array; otherwise derive from permissions objects
  const derivedPermissionCodes =
    role.permissionCodes ??
    (Array.isArray(role.permissions)
      ? role.permissions.map((p) => p.code)
      : []);

  return {
    id: role._id,
    code: role.code,
    // Display role "name" column using the role code
    name: role.code,
    // Prefer new allowedScopeTypes from RBAC API; fall back to legacy scopeType
    scopeType: role.allowedScopeTypes?.[0] ?? role.scopeType ?? "",
    description: role.description ?? "",
    permissionCodes: derivedPermissionCodes,
    // Prefer explicit permissionCount/permissionsCount from API meta, fallback to derived length
    permissionsCount:
      role.permissionCount ??
      role.permissionsCount ??
      derivedPermissionCodes.length,
    createdBy: role.createdByName ?? role.createdBy,
  };
}

export function useAccessManagementRoles() {
  const [roles, setRoles] = useState<RoleFormData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<RoleDrawerMode>("create");
  const [drawerRole, setDrawerRole] = useState<RoleFormData | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [scopeTypeFilter, setScopeTypeFilter] = useState("");

  const fetchRoles = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data, meta } = await listRoles({
        search,
        scopeType: scopeTypeFilter,
        page,
        limit: pageSize,
      });
      setRoles(data.map(mapRoleToFormData));
      setTotalCount(meta.total);
      setTotalPages(meta.pages || 1);
    } catch (err) {
      console.error("Failed to fetch roles:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch roles");
      setRoles([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  }, [search, scopeTypeFilter, page, pageSize]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const handleCreateRole = useCallback(() => {
    setDrawerMode("create");
    setDrawerRole(null);
    setDrawerOpen(true);
  }, []);

  const handleViewRole = useCallback((role: RoleFormData) => {
    setDrawerMode("view");
    setDrawerRole(role);
    setDrawerOpen(true);
  }, []);

  const handleEditRole = useCallback((role: RoleFormData) => {
    setDrawerMode("edit");
    setDrawerRole(role);
    setDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const handleSubmitSuccess = useCallback(() => {
    setDrawerOpen(false);
    setDrawerMode("create");
    setDrawerRole(null);
    fetchRoles();
  }, [fetchRoles]);

  return {
    functions: {
      handleCreateRole,
      handleViewRole,
      handleEditRole,
      handleCloseDrawer,
      handleSubmitSuccess,
    },
    values: {
      roles,
      drawerOpen,
      drawerMode,
      drawerRole,
      page,
      pageSize,
      totalCount,
      totalPages,
      search,
      scopeTypeFilter,
    },
    loading: {
      isPageLoading: isLoading,
    },
    error,
    refetch: fetchRoles,
    setters: {
      setPage,
      setPageSize,
      setSearch,
      setScopeTypeFilter,
    },
  };
}
